import fastDiff from 'fast-diff';
import isUndefined from 'lodash-es/isUndefined';
import { merge, Observable } from 'rxjs';
import { filter, map, mapTo, pairwise, share, startWith, tap } from 'rxjs/operators';
import { hasCodeAncestor } from '../../helpers/dom/code';
import { isPhrasingContent } from '../../helpers/dom/content';
import { findAncestor, isText } from '../../helpers/dom/node';
import { createRangeWithTextRange, createTextRangeWithRange } from '../../helpers/dom/range';
import { patchTextContent, patchTextRange } from '../../helpers/dom/text';
import { findInIterable } from '../../helpers/iterable';
import { lastIndexesOf } from '../../helpers/string';
import { setBoldInRange } from '../../helpers/style/bold';
import { setItalicInRange } from '../../helpers/style/italic';
import { setStrikethroughInRange } from '../../helpers/style/strikethrough';
import { InteractionAction, InteractionHandler, InteractionTrigger } from '../../interactionEvent';
import { createService, Service } from '../base/service';
import { ElementSelection, ElementSelectionService } from '../dom/elementSelectionService';
import { CommitService } from '../text/commitService';
import { UndoService } from '../text/undoService';

const STYLE_DELIMITERS: ReadonlyArray<StyleDelimiterConfig> = [
  {
    delimiter: delimiterRun => delimiterRun === '**' || delimiterRun === '__',
    apply: setBoldInRange,
    action: InteractionAction.TOGGLE_BOLD,
  },
  {
    delimiter: delimiterRun => delimiterRun === '*' || delimiterRun === '_',
    apply: setItalicInRange,
    action: InteractionAction.TOGGLE_ITALIC,
  },
  {
    delimiter: delimiterRun => delimiterRun.includes('~'),
    apply: setStrikethroughInRange,
    action: InteractionAction.TOGGLE_STRIKETHROUGH,
  },
];

type StyleDelimiterConfig = {
  delimiter: (delimiterRun: string) => boolean;
  apply: (container: Node, range: Range) => void;
  action: InteractionAction;
};

export type ElementStyleAsYouTypeOptions = {
  onInteraction?: InteractionHandler;
};

export function createElementStyleAsYouTypeService(
  commitService: CommitService,
  selectionService: ElementSelectionService,
  undoService: UndoService,
  options: ElementStyleAsYouTypeOptions = {},
): Service {
  return createService({
    effects$: createElementStyleAsYouTypeEffect(commitService, selectionService, undoService, options),
  });
}

export function createElementStyleAsYouTypeEffect(
  commitService: CommitService,
  selectionService: ElementSelectionService,
  undoService: UndoService,
  options: ElementStyleAsYouTypeOptions = {},
): Observable<void> {
  const intentFromCommit$ = commitService.changes$.pipe(
    filter(() => commitService.isCommitting),
    mapTo(undefined),
  );

  const intentFromSelection$ = selectionService.changes$.pipe(
    map(() => {
      const { container, selection } = selectionService;
      if (!container || !selection) {
        return undefined;
      }

      // Detect whether the user wants to format-as-you-type by looking for style markup in front of the cursor.
      return computeStyleAsYouTypeIntent(container, selection);
    }),
  );

  return merge(intentFromCommit$, intentFromSelection$).pipe(
    startWith(undefined),
    pairwise(),
    tap(([previousIntent, currentIntent]) => {
      // Apply the format-as-you-type when the cursor has left the style markup.
      if (!previousIntent || currentIntent) {
        return;
      }

      const { container, selection } = selectionService;
      if (!container || !selection) {
        return;
      }

      const newRange = maybeApplyStyleAsYouTypeIntent(
        container,
        selection.range,
        previousIntent,
        undoService,
        options.onInteraction,
      );
      if (!newRange) {
        return;
      }

      selectionService.selectWithRange(newRange, false);
    }),
    mapTo(undefined),
    share(),
  );
}

interface StyleAsYouTypeIntent {
  lineContainer: Node;
  delimiterRun: string;
  endIndex: number;
}

function computeStyleAsYouTypeIntent(
  container: Node,
  selection: ElementSelection,
): StyleAsYouTypeIntent | undefined {
  // Check whether the selection is collapsed (just a cursor) on a text node.
  const { anchorNode, anchorOffset, focusNode, focusOffset, range } = selection;
  if (anchorNode !== focusNode || anchorOffset !== focusOffset || !isText(focusNode)) {
    return undefined;
  }

  // Are we already in inline code or a code block?
  if (hasCodeAncestor(focusNode, container)) {
    return undefined;
  }

  // Check whether we have style markup in front of our cursor.
  const textBeforeCursor = focusNode.data.slice(0, focusOffset);
  const delimiterRunMatch = /\S([*_~]+)$/.exec(textBeforeCursor);
  if (!delimiterRunMatch || delimiterRunMatch.length < 2) {
    return undefined;
  }

  const lineContainer = findAncestor(focusNode, node => !isPhrasingContent(node), container);
  if (!lineContainer) {
    return undefined;
  }

  const lineTextRange = createTextRangeWithRange(lineContainer, range);

  // This shouldn't happen since we have a collapsed cursor.
  if (!lineTextRange || lineTextRange.startIndex !== lineTextRange.endIndex) {
    // eslint-disable-next-line no-console
    console.warn('Unexpected text range for collapsed selection in style-as-you-type effect');
    return undefined;
  }

  const [, delimiterRun] = delimiterRunMatch;
  return {
    lineContainer,
    delimiterRun,
    endIndex: lineTextRange.endIndex,
  };
}

function maybeApplyStyleAsYouTypeIntent(
  container: Node,
  cursorRange: Range,
  intent: StyleAsYouTypeIntent,
  undoService: UndoService,
  onInteraction?: InteractionHandler,
): Range | undefined {
  const { lineContainer, delimiterRun, endIndex } = intent;

  // Sanity check that our line of text is still in the editor.
  if (!container.contains(lineContainer)) {
    return undefined;
  }

  const lineText = lineContainer.textContent || '';
  const closingDelimiterStartIndex = endIndex - delimiterRun.length;

  // Sanity check that our style markup is still where we expect it to be.
  if (lineText.slice(closingDelimiterStartIndex, endIndex) !== delimiterRun) {
    return undefined;
  }

  // Now that the cursor moved away from the style markup, verify that it is a legal right-flanking delimiter.
  const beforeClosingDelimiterText = lineText.substring(
    closingDelimiterStartIndex - 1,
    closingDelimiterStartIndex,
  );
  const afterClosingDelimiterText = lineText.substring(endIndex, endIndex + 1);
  if (!canCloseEmphasis(beforeClosingDelimiterText, delimiterRun, afterClosingDelimiterText)) {
    return undefined;
  }

  // Search backwards for a matching left-flanking delimiter, requiring at least one character in between the opening
  // and closing delimiters.
  const candidateIndexes = lastIndexesOf(lineText, delimiterRun, closingDelimiterStartIndex - 2);
  const startIndex = findInIterable(candidateIndexes, candidateIndex => {
    const openingDelimiterEndIndex = candidateIndex + delimiterRun.length;
    const beforeOpeningDelimiterText = lineText.substring(candidateIndex - 1, candidateIndex);
    const afterOpeningDelimiterText = lineText.substring(
      openingDelimiterEndIndex,
      openingDelimiterEndIndex + 1,
    );

    return canOpenEmphasis(beforeOpeningDelimiterText, delimiterRun, afterOpeningDelimiterText);
  });
  if (isUndefined(startIndex)) {
    return undefined;
  }

  // Allow the user to undo this.
  undoService.takeAndCommitSnapshot();

  // Before making any changes, compute the cursor offset in the line text.
  const cursorTextRange = createTextRangeWithRange(lineContainer, cursorRange);

  // Remove the style markup from the text.
  const textPatch: readonly fastDiff.Diff[] = [
    [fastDiff.EQUAL, lineText.slice(0, startIndex)],
    [fastDiff.DELETE, delimiterRun],
    [fastDiff.EQUAL, lineText.slice(startIndex + delimiterRun.length, endIndex - delimiterRun.length)],
    [fastDiff.DELETE, delimiterRun],
    [fastDiff.EQUAL, lineText.slice(endIndex)],
  ];
  const didPatchSucceed = patchTextContent(lineContainer, textPatch);
  if (!didPatchSucceed) {
    return undefined;
  }

  // Apply the style to the text.
  const styleRange = createRangeWithTextRange(lineContainer, {
    startIndex,
    endIndex: endIndex - delimiterRun.length * 2,
  });
  // This shouldn't happen since we know these start/end indexes are in range.
  if (!styleRange) {
    // eslint-disable-next-line no-console
    console.warn('Failed to create range from text range for applying style-as-you-type');
    return undefined;
  }

  const delimiterConfig = STYLE_DELIMITERS.find(config => config.delimiter(delimiterRun));
  if (delimiterConfig) {
    delimiterConfig.apply(container, styleRange);

    if (onInteraction) {
      onInteraction({
        trigger: InteractionTrigger.KEYBOARD_TYPING,
        action: delimiterConfig.action,
      });
    }
  }

  // If the cursor wasn't in this line, nothing else to do.
  if (!cursorTextRange) {
    return undefined;
  }

  // Return the new cursor range/offset corrected for the text changes.
  const newCursorTextRange = patchTextRange(cursorTextRange, textPatch);
  return createRangeWithTextRange(lineContainer, newCursorTextRange) || undefined;
}

function isUnderscoreDelimiter(delimiterRun: string) {
  return /^_+$/.test(delimiterRun);
}

// https://spec.commonmark.org/0.29/#can-open-emphasis
function canOpenEmphasis(precedingText: string, delimiterRun: string, followingText: string) {
  if (isUnderscoreDelimiter(delimiterRun)) {
    return (
      isLeftFlankingDelimiter(precedingText, followingText) &&
      (!isRightFlankingDelimiter(precedingText, followingText) || isPrecedingPunctuation(precedingText))
    );
  }

  return isLeftFlankingDelimiter(precedingText, followingText);
}

// https://spec.commonmark.org/0.29/#left-flanking-delimiter-run
function isLeftFlankingDelimiter(precedingText: string, followingText: string) {
  const isFollowedByWhitespace = /^(\s|$)/.test(followingText);
  const isFollowedByPunctuation = isFollowingPunctuation(followingText);

  return (
    !isFollowedByWhitespace &&
    (!isFollowedByPunctuation || (isFollowedByPunctuation && /(^|[\s\p{P}])$/u.test(precedingText)))
  );
}

function isFollowingPunctuation(followingText: string) {
  return /^\p{P}/u.test(followingText);
}

// https://spec.commonmark.org/0.29/#can-close-emphasis
function canCloseEmphasis(precedingText: string, delimiterRun: string, followingText: string) {
  if (isUnderscoreDelimiter(delimiterRun)) {
    return (
      isRightFlankingDelimiter(precedingText, followingText) &&
      (!isLeftFlankingDelimiter(precedingText, followingText) || isFollowingPunctuation(followingText))
    );
  }

  return isRightFlankingDelimiter(precedingText, followingText);
}

// https://spec.commonmark.org/0.29/#right-flanking-delimiter-run
function isRightFlankingDelimiter(precedingText: string, followingText: string) {
  const isPrecededByWhitespace = /(^|\s)$/.test(precedingText);
  const isPrecededByPunctuation = isPrecedingPunctuation(precedingText);

  return (
    !isPrecededByWhitespace &&
    (!isPrecededByPunctuation || (isPrecededByPunctuation && /^([\s\p{P}]|$)/u.test(followingText)))
  );
}

function isPrecedingPunctuation(precedingText: string) {
  return /\p{P}$/u.test(precedingText);
}
